import React, { Suspense, useEffect, useRef } from 'react';
import { Route, Routes, useLocation, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import useSocketNotifications from '../hooks/useSocketNotifications';
import { APP_ROUTES, PUBLIC_ROUTES, REDIRECT_MAP } from './config';
import Navbar from '../layouts/Navbar';
import Sidebar from '../layouts/Sidebar';
import { Notfound } from './pages';
import { isTokenExpired } from '../utils/auth';
import { logoutUser, refreshUserInfo } from '../store/auth/authSlice';
import { useValidateSessionQuery } from '../services/api/user';
import usePageTitle from '../hooks/usePageTitle';
import { PAGE_CLASSIFICATION } from '../constants';
import {
  isClassificationEnabled,
  isFeatureEnabled,
} from '../utils/routeAccess';
import Loader from '../components/Loader';

const PUBLIC_PAGES = new Set(Object.keys(PUBLIC_ROUTES));

const routeFallback = (
  <div className="flex h-full items-center justify-center">
    <Loader />
  </div>
);

function AppRoutes() {
  const auth = useSelector((state) => state.auth);
  const token = auth?.token;
  const userType = auth?.user?.user_type;
  const clientId = auth?.user?.client_id;
  const clientName = auth?.userInfo?.client_name;
  const clientLogo = auth?.userInfo?.client_logo;

  usePageTitle({
    clientName,
    clientLogo,
    userType,
  });

  useSocketNotifications(token);

  const location = useLocation();
  const dispatch = useDispatch();
  const tokenExpired = Boolean(token) && isTokenExpired(token);

  useEffect(() => {
    if (tokenExpired) {
      dispatch(logoutUser());
    }
  }, [location.pathname, tokenExpired, dispatch]);

  const {
    data: session,
    error: sessionError,
    isLoading: isValidatingSession,
  } = useValidateSessionQuery(undefined, {
    skip: !token || tokenExpired,
    refetchOnMountOrArgChange: 60,
    refetchOnFocus: false,
  });

  const userInfoRef = useRef(auth?.userInfo);
  userInfoRef.current = auth?.userInfo;

  useEffect(() => {
    if (session?.status && session.userInfo) {
      const currentConfig = JSON.stringify(userInfoRef.current || {});
      const newConfig = JSON.stringify(session.userInfo || {});
      if (currentConfig !== newConfig) {
        dispatch(refreshUserInfo(session.userInfo));
      }
    }
  }, [session, dispatch]);

  useEffect(() => {
    if (sessionError?.status === 401 || sessionError?.status === 403) {
      dispatch(logoutUser());
    }
  }, [sessionError, dispatch]);

  if (token && !userType) {
    return routeFallback;
  }

  if (token && !tokenExpired && isValidatingSession) {
    return routeFallback;
  }

  const clientConfig = auth?.userInfo?.client_config;
  const predictionEnabled = isFeatureEnabled(
    PAGE_CLASSIFICATION.infestationPrediction,
    { clientConfig, userRole: userType },
  );
  const homePath =
    REDIRECT_MAP[userType] ??
    (predictionEnabled ? '/maps' : '/observations');

  const isPublicPage = PUBLIC_PAGES.has(location.pathname);
  if (!token && !isPublicPage) {
    return <Navigate to="/sign-in" replace />;
  }

  if (token && location.pathname === '/sign-in') {
    return <Navigate to={homePath} replace />;
  }

  const accessibleRoutes = APP_ROUTES.filter((route) => {
    if (!route.roles) {
      console.warn(`Route ${route.path} missing roles`);
      return false;
    }
    return route.roles.includes(userType);
  }).map((route) =>
    isClassificationEnabled(route, { clientConfig, userRole: userType })
      ? route
      : {
          ...route,
          element: <Notfound />,
        },
  );

  if (
    token &&
    homePath !== '/' &&
    (location.pathname === '/' ||
      (location.pathname === '/maps' && !predictionEnabled))
  ) {
    return <Navigate to={homePath} replace />;
  }

  return (
    <div
      className={`flex flex-col h-screen ${isPublicPage ? '' : 'bg-gray-100'}`}
    >
      {!isPublicPage && <Navbar />}
      <div className="flex flex-grow">
        {!isPublicPage && <Sidebar userRole={userType} client_id={clientId} />}
        <main
          className={
            isPublicPage
              ? 'flex-grow h-screen'
              : 'flex-grow h-[calc(100vh-5rem)] py-2 overflow-y-scroll'
          }
        >
          <Suspense fallback={routeFallback}>
            <Routes>
              {Object.entries(PUBLIC_ROUTES).map(([path, element]) => (
                <Route key={path} path={path} element={element} />
              ))}
              {accessibleRoutes.map((route) => (
                <Route
                  key={route.path}
                  path={route.path}
                  element={route.element}
                />
              ))}
              <Route path="*" element={<Notfound />} />
            </Routes>
          </Suspense>
        </main>
      </div>
    </div>
  );
}

export default AppRoutes;
